import {Injectable} from "@angular/core";
import {createState, select, Store, withProps} from "@ngneat/elf";
import {localStorageStrategy, persistState} from "@ngneat/elf-persist-state";
import {AuthMethod, AuthTransitionStatus, User} from "@app/interfaces";

interface Settings {
  hasSeenOnboarding: boolean,
  news_updated_at: string | null,
  events_updated_at: string | null,
  places_updated_at: string | null,
  articles_updated_at: string | null,
  article_categories_updated_at: string | null,
  polls_updated_at: string | null,
  user: User | null,
  accessToken: string | null,
  authMethod: AuthMethod,
  authTransitionStatus: AuthTransitionStatus,
}

const {state, config} = createState(withProps<Settings>({
  hasSeenOnboarding: false,
  news_updated_at: null,
  events_updated_at: null,
  places_updated_at: null,
  articles_updated_at: null,
  article_categories_updated_at: null,
  polls_updated_at: null,
  user: null,
  accessToken: null,
  authMethod: null,
  authTransitionStatus: 'idle',
}));
const settingsStore = new Store({name: 'settings', state, config});

const persist = persistState(settingsStore, {
  key: 'settings',
  storage: localStorageStrategy,
});

@Injectable({providedIn: 'root'})
export class SettingsRepository {
  settings$ = settingsStore.pipe(select((state) => state));
  newsUpdatedAt$ = settingsStore.pipe(select((state) => state.news_updated_at));
  eventsUpdatedAt$ = settingsStore.pipe(select((state) => state.events_updated_at));
  placesUpdatedAt$ = settingsStore.pipe(select((state) => state.places_updated_at));
  articlesUpdatedAt$ = settingsStore.pipe(select((state) => state.articles_updated_at));
  articleCategoriesUpdatedAt$ = settingsStore.pipe(select((state) => state.article_categories_updated_at));
  pollsUpdatedAt$ = settingsStore.pipe(select((state) => state.polls_updated_at));
  authMethod$ = settingsStore.pipe(select((state) => state.authMethod));
  authTransitionStatus$ = settingsStore.pipe(select((state) => state.authTransitionStatus));

  setHasSeenOnboarding() {
    settingsStore.update((state) => ({
      ...state,
      hasSeenOnboarding: true,
    }));
  }

  setNewsUpdatedAt(date: string = new Date().toISOString()) {
    settingsStore.update((state) => ({
      ...state,
      news_updated_at: date,
    }));
  }

  removeNewsUpdatedAt() {
    settingsStore.update((state) => ({
      ...state,
      news_updated_at: null,
    }));
  }

  setEventsUpdatedAt(date: string = new Date().toISOString()) {
    settingsStore.update((state) => ({
      ...state,
      events_updated_at: date,
    }));
  }

  removeEventsUpdatedAt() {
    settingsStore.update((state) => ({
      ...state,
      events_updated_at: null,
    }));
  }

  setPlacesUpdatedAt(date: string = new Date().toISOString()) {
    settingsStore.update((state) => ({
      ...state,
      places_updated_at: date,
    }));
  }

  removePlacesUpdatedAt() {
    settingsStore.update((state) => ({
      ...state,
      places_updated_at: null,
    }));
  }

  setArticlesUpdatedAt(date: string = new Date().toISOString()) {
    settingsStore.update((state) => ({
      ...state,
      articles_updated_at: date,
    }));
  }

  removeArticlesUpdatedAt() {
    settingsStore.update((state) => ({
      ...state,
      articles_updated_at: null,
    }));
  }

  setArticleCategoriesUpdatedAt(date: string = new Date().toISOString()) {
    settingsStore.update((state) => ({
      ...state,
      article_categories_updated_at: date,
    }));
  }

  removeArticleCategoriesUpdatedAt() {
    settingsStore.update((state) => ({
      ...state,
      article_categories_updated_at: null,
    }));
  }

  setPollsUpdatedAt(date: string = new Date().toISOString()) {
    settingsStore.update((state) => ({
      ...state,
      polls_updated_at: date,
    }));
  }

  removePollsUpdatedAt() {
    settingsStore.update((state) => ({
      ...state,
      polls_updated_at: null,
    }));
  }

  setUser(user: User) {
    settingsStore.update((state) => ({
      ...state,
      user,
    }));
  }

  removeUser() {
    settingsStore.update((state) => ({
      ...state,
      user: null,
    }));
  }

  setAccessToken(accessToken: string) {
    settingsStore.update((state) => ({
      ...state,
      accessToken,
    }));
  }

  setAuthMethod(authMethod: AuthMethod) {
    settingsStore.update((state) => ({
      ...state,
      authMethod,
    }));
  }

  setAuthTransitionStatus(authTransitionStatus: AuthTransitionStatus) {
    settingsStore.update((state) => ({
      ...state,
      authTransitionStatus,
    }));
  }

  removeAccessToken() {
    settingsStore.update((state) => ({
      ...state,
      accessToken: null,
    }));
  }

  resetAuthState() {
    settingsStore.update((state) => ({
      ...state,
      accessToken: null,
      authMethod: null,
      authTransitionStatus: 'idle',
    }));
  }

  constructor() {
    console.log(`${this.constructor.name}.constructor`, settingsStore);
    this.settings$.subscribe((settings) => {
      console.log(`${this.constructor.name}.settings$`, settings);
    });
  }

  updateSettings(settings: Settings) {
    console.log(`${this.constructor.name}.updateSettings`, settings);
  }
}
